"use client";

import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { ChevronLeft, ChevronRight, X } from '@/components/icons';

interface ImageViewerProps {
  imageUrl?: string | null;
  imageUrls?: string[];
  startIndex?: number;
  isOpen: boolean;
  onClose: () => void;
}

const ImageViewer: React.FC<ImageViewerProps> = ({ imageUrl, imageUrls, startIndex = 0, isOpen, onClose }) => {
  const urls = React.useMemo(
    () => imageUrls && imageUrls.length > 0 ? imageUrls : imageUrl ? [imageUrl] : [],
    [imageUrls, imageUrl]
  );
  const [currentIndex, setCurrentIndex] = React.useState<number>(Math.max(0, Math.min(startIndex, urls.length - 1)));
  const [displayedUrl, setDisplayedUrl] = React.useState<string | null>(null);
  const [isLoadingImage, setIsLoadingImage] = React.useState<boolean>(false);
  const [imageVisible, setImageVisible] = React.useState<boolean>(false);
  const [animKey, setAnimKey] = React.useState<number>(0);
  const prevIsOpenRef = React.useRef<boolean>(isOpen);

  // Only reset currentIndex when isOpen changes from false to true
  React.useEffect(() => {
    if (isOpen && !prevIsOpenRef.current) {
      // Opening the viewer: reset to startIndex
      setCurrentIndex(Math.max(0, Math.min(startIndex, Math.max(0, urls.length - 1))));
    }
    prevIsOpenRef.current = isOpen;
  }, [isOpen, startIndex, urls.length]);

  const goPrev = React.useCallback(() => setCurrentIndex(i => i === 0 ? urls.length - 1 : Math.max(0, i - 1)), [urls.length]);
  const goNext = React.useCallback(() => setCurrentIndex(i => i === urls.length - 1 ? 0 : Math.min(urls.length - 1, i + 1)), [urls.length]);

  const currentUrl = urls[currentIndex] || null;

  // Preload and fade strategy: preload the image before swapping src to avoid blinking
  React.useEffect(() => {
    if (!isOpen || !currentUrl) return;
    setIsLoadingImage(true);
    setImageVisible(false);
    setAnimKey(k => k + 1); // trigger animation key change
    let cancelled = false;
    const img = new Image();
    img.onload = () => {
      if (cancelled) return;
      setDisplayedUrl(currentUrl);
      // small timeout to allow render and then fade in
      requestAnimationFrame(() => requestAnimationFrame(() => setImageVisible(true)));
      setIsLoadingImage(false);
    };
    img.onerror = () => {
      if (cancelled) return;
      setIsLoadingImage(false);
      setDisplayedUrl(currentUrl); // still attempt to show broken image
      setImageVisible(true);
    };
    img.src = currentUrl;
    return () => { cancelled = true; };
  }, [currentUrl, isOpen]);

  React.useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') return onClose();
      if (e.key === 'ArrowLeft') { e.preventDefault(); goPrev(); }
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        setCurrentIndex(i => i === urls.length - 1 ? 0 : Math.min(urls.length - 1, i + 1));
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onClose, goPrev, urls.length]);

  // simple swipe support
  const touchStartX = React.useRef<number | null>(null);
  const touchDeltaX = React.useRef<number>(0);
  const handleTouchStart = (e: React.TouchEvent) => { if (e.touches.length > 1) return; touchStartX.current = e.touches[0].clientX; touchDeltaX.current = 0; };
  const handleTouchMove = (e: React.TouchEvent) => { if (!touchStartX.current || e.touches.length > 1) return; touchDeltaX.current = e.touches[0].clientX - (touchStartX.current || 0); };
  const handleTouchEnd = () => { const threshold = 60; if (touchDeltaX.current > threshold) goPrev(); else if (touchDeltaX.current < -threshold) goNext(); touchStartX.current = null; touchDeltaX.current = 0; };

  // trackpad / wheel support for macOS and others
  const lastWheelRef = React.useRef<number>(0);
  const handleWheel = (e: React.WheelEvent) => {
    try {
      const now = Date.now();
      if (now - lastWheelRef.current < 100) return;
      const dx = e.deltaX !== 0 ? e.deltaX : (e.shiftKey ? e.deltaY : 0);
      const dy = e.deltaY;
      if (Math.abs(dx) < Math.abs(dy) && !e.shiftKey) return;
      if (Math.abs(dx) < 8) return;
      e.preventDefault();
      lastWheelRef.current = now;
      // wheel horizontal positive -> next, negative -> prev
      if (dx > 0) goNext(); else goPrev();
    } catch (_) {}
  };

  // share removed per request

  // focus management: keep focus off the close button when opening
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const [headerHeight, setHeaderHeight] = React.useState<number>(0);
  const [isDesktop, setIsDesktop] = React.useState<boolean>(typeof window !== 'undefined' ? window.innerWidth >= 1024 : false);

  // Calculate header height dynamically (desktop only)
  React.useEffect(() => {
    const update = () => {
      const header = document.querySelector('header.app-header');
      setHeaderHeight(header ? header.getBoundingClientRect().height : 0);
      setIsDesktop(window.innerWidth >= 1024);
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);
  const isPointerOverRef = React.useRef(false);
  const previousActiveElementRef = React.useRef<HTMLElement | null>(null);

  // 🎯 PERFORMANCE FIX: Don't use global listeners that affect page scroll.
  // Only block events that are INSIDE the viewer element itself.
  // This keeps desktop scroll smooth and native.

  // Allow clicking outside the viewer to interact with the app or close the viewer (desktop)
  React.useEffect(() => {
    if (!isOpen) return;

    const handler = (e: PointerEvent) => {
      try {
        const target = e.target as Node | null;
        // If click is inside the viewer container, ignore
        if (containerRef.current && target && containerRef.current.contains(target)) {
          return;
        }
        // Otherwise, close the viewer but DO NOT stop propagation so underlying element still receives the event
        onClose();
      } catch (_) {}
    };

    // Use capture so we can close before other handlers if needed but allow event to continue
    document.addEventListener('pointerdown', handler, true);
    return () => document.removeEventListener('pointerdown', handler, true);
  }, [isOpen, onClose]);

  // Focus management y limpieza completa al abrir/cerrar
  React.useEffect(() => {
    if (!isOpen) {
      // Al cerrar: restaurar focus al elemento anterior si existe
      if (previousActiveElementRef.current && typeof previousActiveElementRef.current.focus === 'function') {
        try {
          // Usar setTimeout para asegurar que el modal esté completamente cerrado
          setTimeout(() => {
            previousActiveElementRef.current?.focus({ preventScroll: true });
            previousActiveElementRef.current = null;
          }, 100);
        } catch (e) {}
      }
      // Asegurar que isPointerOverRef se resetea
      isPointerOverRef.current = false;
      return;
    }
    
    // Al abrir: guardar el elemento activo actual
    try {
      previousActiveElementRef.current = document.activeElement as HTMLElement;
    } catch (e) {}
    
    // Focus the container after open para que el botón de cerrar no reciba focus automático
    const t = setTimeout(() => {
      if (containerRef.current) {
        containerRef.current.focus({ preventScroll: true });
      }
    }, 50);
    
    return () => clearTimeout(t);
  }, [isOpen]);

  if (!isOpen || urls.length === 0) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent hideClose fullScreen className="p-0 m-0 border-none w-screen h-screen max-w-none max-h-none">
        <div
          ref={containerRef}
          tabIndex={-1}
          className="relative w-full h-full flex items-center justify-center"
          style={{
            // Stronger glass-like blurred backdrop but more transparent
            background: 'rgba(4,6,9,0.28)',
            backdropFilter: 'blur(26px) saturate(120%)',
            WebkitBackdropFilter: 'blur(26px) saturate(120%)',
            opacity: isOpen ? 1 : 0,
            transition: 'opacity 320ms cubic-bezier(.4,2,.3,1)',
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100vw',
            height: '100vh',
            zIndex: 9999,
            outline: 'none',
            boxShadow: 'none',
            WebkitTapHighlightColor: 'transparent',
            pointerEvents: 'none',
            paddingTop: 'env(safe-area-inset-top)',
          }}
          onFocus={(e) => { (e.currentTarget as HTMLDivElement).style.outline = 'none'; }}
        >
          <DialogHeader className="sr-only">
            <DialogTitle>Visor de imagen</DialogTitle>
            <DialogDescription>Navega las imágenes con las flechas o deslizando. Presiona Escape para cerrar.</DialogDescription>
          </DialogHeader>

          <div className="absolute right-4 z-50" style={{ pointerEvents: 'auto', top: 'calc(1rem + env(safe-area-inset-top))' }}>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={(e) => { e.stopPropagation(); onClose(); }}
                    className="p-1 bg-primary text-primary-foreground rounded-lg transition-colors hover:bg-primary/90"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent variant="sidebar">
                  <p>Cerrar</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>

          <div className="absolute left-1/2 -translate-x-1/2 z-50" style={{ pointerEvents: 'auto', top: 'calc(1rem + env(safe-area-inset-top))' }}>
            <div className="bg-black/60 text-white px-3 py-1 rounded-md text-sm">{currentIndex + 1} / {urls.length}</div>
          </div>

          {/* prev/next buttons */}
          {urls.length > 1 && (
            <>
              {currentIndex > 0 && (
                <button aria-label="Anterior" onClick={goPrev} className="absolute left-6 top-1/2 -translate-y-1/2 z-50 inline-flex items-center justify-center h-8 w-8 rounded-full bg-primary text-primary-foreground active:scale-95 active:shadow-inner transition-transform" style={{ pointerEvents: 'auto' }}>
                  <ChevronLeft className="h-3 w-3" />
                </button>
              )}
              {currentIndex < urls.length - 1 ? (
                <button aria-label="Siguiente" onClick={goNext} className="absolute right-6 top-1/2 -translate-y-1/2 z-50 inline-flex items-center justify-center h-8 w-8 rounded-full bg-primary text-primary-foreground active:scale-95 active:shadow-inner transition-transform" style={{ pointerEvents: 'auto' }}>
                  <ChevronRight className="h-3 w-3" />
                </button>
              ) : (
                <button
                  aria-label="Volver al principio"
                  title="Volver al principio"
                  onClick={() => setCurrentIndex(0)}
                  className="absolute right-6 top-1/2 -translate-y-1/2 z-50 inline-flex items-center justify-center h-8 w-8 rounded-full bg-primary text-primary-foreground active:scale-95 active:shadow-inner transition-transform"
                  style={{ pointerEvents: 'auto' }}
                >
                  <svg className="h-3 w-3 sm:h-4 sm:w-4" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                    <path d="M17 6 L11 12 L17 18" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M11 6 L5 12 L11 18" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              )}
            </>
          )}

          <div
            className="w-screen h-screen flex items-center justify-center overscroll-contain"
            onClick={(e) => {
              // Close only if clicking the background (not the image)
              if (e.target === e.currentTarget) {
                onClose();
              }
            }}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onWheel={handleWheel}
            onPointerEnter={() => { isPointerOverRef.current = true; }}
            onPointerLeave={() => { isPointerOverRef.current = false; }}
            onPointerCancel={() => { isPointerOverRef.current = false; }}
          >
            <div className="relative w-full h-full flex items-center justify-center overflow-auto" style={{ pointerEvents: 'auto' }}>
              {isLoadingImage && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-14 h-14 rounded-full border-2 border-white/30 animate-spin" />
                </div>
              )}
              {displayedUrl && (
                <img
                  key={animKey}
                  src={displayedUrl}
                  alt={`Imagen ${currentIndex + 1} de ${urls.length}`}
                  className="block transition-all duration-300"
                  style={{
                    display: imageVisible ? 'block' : 'block',
                    opacity: imageVisible ? 1 : 0,
                    transform: imageVisible
                      ? 'scale(1)'
                      : 'scale(0.92)',
                    transition: 'opacity 220ms ease, transform 320ms cubic-bezier(.4,2,.3,1)',
                    width: '100%',
                    height: '100%',
                    maxWidth: '100vw',
                    maxHeight: '100vh',
                    objectFit: 'contain',
                    margin: '0 auto',
                    padding: '0',
                  }}
                />
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
export default ImageViewer;
